import React, { useEffect, useMemo, useState } from "react";
import { Mail, MailOpen, Loader2, Trash2 } from "lucide-react";
import { api } from "../../adminApi";
import { Card, SectionTitle } from "../../components/admin/AdminUI";

const FILTERS = [
  { id: "all", label: "TÜMÜ" },
  { id: "unread", label: "OKUNMAMIŞ" },
  { id: "read", label: "OKUNMUŞ" },
];

export default function AdminEnquiries() {
  const [list, setList] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = () => api.enquiries().then(setList).catch(() => setList([]));
  useEffect(() => {
    load();
  }, []);

  const unread = useMemo(() => (list || []).filter((m) => !m.read).length, [list]);
  const shown = useMemo(
    () =>
      (list || []).filter((m) =>
        filter === "all" ? true : filter === "unread" ? !m.read : !!m.read
      ),
    [list, filter]
  );

  const toggleRead = async (m) => {
    const saved = await api.setEnquiryRead(m.id, !m.read);
    setList((l) => l.map((x) => (x.id === saved.id ? saved : x)));
    window.dispatchEvent(new Event("hrd-unread-changed"));
  };

  const remove = async (m) => {
    if (!window.confirm(`"${m.name}" mesajını silmek istediğinize emin misiniz?`)) return;
    await api.deleteEnquiry(m.id);
    setList((l) => l.filter((x) => x.id !== m.id));
    window.dispatchEvent(new Event("hrd-unread-changed"));
  };

  return (
    <div data-testid="admin-enquiries">
      <SectionTitle
        label="[ İLETİŞİM FORMU ]"
        title="Mesajlar ve teklif talepleri"
        right={
          <span
            data-testid="enquiries-unread-badge"
            className={`mono text-[9px] px-3.5 py-2.5 border ${
              unread ? "bg-[#141414] text-[#FAFAFA] border-[#141414]" : "text-[#6B675F] border-[#E2E0D8]"
            }`}
          >
            {unread} OKUNMAMIŞ
          </span>
        }
      />

      <div className="flex flex-wrap gap-2 mb-6">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`enquiries-filter-${f.id}`}
            className={`mono text-[9px] px-4 py-2.5 border transition-colors ${
              filter === f.id
                ? "bg-[#141414] text-[#FAFAFA] border-[#141414]"
                : "bg-white text-[#141414] border-[#E2E0D8] hover:border-[#C8B29B]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {!list ? (
        <Loader2 size={18} className="animate-spin text-[#8C7862]" />
      ) : shown.length === 0 ? (
        <Card className="py-16 text-center">
          <Mail size={20} strokeWidth={1.4} className="text-[#A8A29A] mx-auto mb-4" />
          <p className="mono text-[9.5px] text-[#6B675F]">MESAJ YOK</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {shown.map((m) => (
            <Card
              key={m.id}
              data-testid={`enquiry-${m.id}`}
              className={`p-6 relative ${m.read ? "" : "border-l-2 border-l-[#8C7862]"}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2.5">
                    {!m.read && <span className="w-2 h-2 rounded-full bg-[#8C7862] shrink-0" />}
                    <p className={`text-[17px] ${m.read ? "text-[#141414]/80" : "text-[#141414] font-medium"}`}>
                      {m.name}
                    </p>
                  </div>
                  <a href={`mailto:${m.email}`} className="mono text-[9.5px] text-[#8C7862] link-underline">
                    {m.email}
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <p className="mono text-[9px] text-[#A8A29A] mr-2">
                    {m.created_at ? new Date(m.created_at).toLocaleString("tr-TR") : ""}
                  </p>
                  <button
                    onClick={() => toggleRead(m)}
                    data-testid={`enquiry-toggle-read-${m.id}`}
                    title={m.read ? "Okunmadı olarak işaretle" : "Okundu olarak işaretle"}
                    className="inline-flex items-center gap-2 mono text-[8.5px] border border-[#E2E0D8] bg-white px-3 py-2 hover:border-[#C8B29B] transition-colors"
                  >
                    {m.read ? <MailOpen size={12} strokeWidth={1.7} /> : <Mail size={12} strokeWidth={1.7} />}
                    {m.read ? "OKUNMADI YAP" : "OKUNDU YAP"}
                  </button>
                  <button
                    onClick={() => remove(m)}
                    data-testid={`enquiry-delete-${m.id}`}
                    title="Sil"
                    className="w-9 h-9 border border-[#E2E0D8] bg-white flex items-center justify-center text-[#8C2E1D] hover:border-[#D9B7AE] transition-colors"
                  >
                    <Trash2 size={13} strokeWidth={1.7} />
                  </button>
                </div>
              </div>

              <div className="flex flex-wrap gap-x-6 gap-y-1 mt-4">
                {[
                  ["Firma", m.company],
                  ["Sistem", m.system],
                  ["Alan (m2)", m.area],
                  ["Dil", (m.lang || "").toUpperCase()],
                ]
                  .filter(([, v]) => v)
                  .map(([k, v]) => (
                    <p key={k} className="mono text-[9px] text-[#6B675F]">
                      {k.toUpperCase()}: <span className="text-[#141414]">{v}</span>
                    </p>
                  ))}
              </div>
              <p className="text-[14.5px] text-[#141414]/75 leading-relaxed mt-5 border-t border-[#E2E0D8] pt-5 whitespace-pre-wrap">
                {m.message}
              </p>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
